import './dropdown.scss';

import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

import { FormInput } from '../input';

const Dropdown = ({
  placeholder,
  name,
  register,
  options,
  error,
  defaultValue,
  formLabel,
  disabled,
  readOnly,
  key,
  onSelect
}) => {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState('');
  const [dropdownValue, setDropdownValue] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!dropdownValue && defaultValue) {
      const defaultSelected = options.find(({ value }) => value === defaultValue);
      setSelected(() => defaultSelected?.label || '');
      setDropdownValue(() => defaultSelected?.label || '');
    }
  }, [options]);

  useEffect(() => {
    setSelected(() => '');
    setDropdownValue(() => '');
  }, [key]);

  useEffect(() => {
    if (!dropdownValue && defaultValue) {
      const defaultSelected = options.find(({ value }) => value === defaultValue);
      if (defaultSelected) {
        setSelected(defaultSelected.label || '');
        setDropdownValue(defaultSelected.label || '');
      }
    }
  }, [defaultValue]);

  // Close dropdown when clicking outside — uses native DOM check instead of
  // react-click-away-listener to avoid portal race conditions.
  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handleItemClick = (value, label) => {
    setSelected(label);

    if (typeof label === 'object') {
      const labelToShow = label.props.children.find(
        (item) => typeof item !== 'object' && item !== ' '
      );
      setDropdownValue(labelToShow);
    } else {
      setDropdownValue(label);
    }

    setOpen(false);

    if (onSelect) {
      onSelect(value);
    } else {
      register.onChange({ target: { name, value } });
    }
  };

  const handleChange = (e) => {
    setDropdownValue(e.target.value);
    setFilteredOptions(
      options.filter((option) => {
        const { label } = option;
        let theLabel;

        if (typeof label === 'object') {
          theLabel = label.props.children.find((item) => typeof item !== 'object' && item !== ' ');
        } else {
          theLabel = label;
        }

        return theLabel.toLowerCase().includes(e.target.value.toLowerCase());
      })
    );
  };

  return (
    <div
      ref={dropdownRef}
      className={classNames({
        dropdown: true,
        'dropdown--slim': !formLabel
      })}>
      <FormInput
        label={formLabel}
        placeholder={placeholder}
        name={`display-${name}`}
        type="text"
        value={dropdownValue}
        onClick={() => {
          setOpen(!open);
          setFilteredOptions(options);
        }}
        error={error}
        onChange={handleChange}
        role="button"
        readOnly={disabled || readOnly}
      />

      <DropdownMenu
        open={open}
        options={filteredOptions}
        name={name}
        selected={selected}
        onItemClick={handleItemClick}
        disabled={readOnly}
      />
    </div>
  );
};

Dropdown.propTypes = {
  name: PropTypes.string,
  placeholder: PropTypes.string,
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  options: PropTypes.array,
  register: PropTypes.object,
  error: PropTypes.string,
  formLabel: PropTypes.node,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  key: PropTypes.string,
  onSelect: PropTypes.func
};

const DropdownMenu = React.forwardRef(
  ({ name, options, selected, onItemClick, handleSelect, open, isFilter, disabled }, ref) => {
    return (
      <ul
        ref={ref}
        role="tree"
        className={classNames({
          dropdown__menu: true,
          open
        })}>
        {options.map(({ value, label }, index) => (
          <li
            className="dropdown__menu-item"
            key={index}
            role="treeitem"
            data-testid={`dropdown-option-${value}`}
            onClick={() => {
              if (disabled || label === selected) return;
              if (onItemClick) {
                onItemClick(value, label);
              } else if (handleSelect) {
                handleSelect({ target: { value } }, label);
              }
            }}>
            <label className={classNames({ checked: selected === label })}>{label}</label>
          </li>
        ))}
      </ul>
    );
  }
);

DropdownMenu.displayName = 'DropdownMenu';

DropdownMenu.propTypes = {
  name: PropTypes.string,
  options: PropTypes.array,
  selected: PropTypes.string,
  onItemClick: PropTypes.func,
  handleSelect: PropTypes.func,
  open: PropTypes.bool,
  isFilter: PropTypes.bool,
  disabled: PropTypes.bool
};
export { Dropdown, DropdownMenu };
